import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { useEffectButNotOnMount } from '@hooks/useEffectButNotOnMount';
import { RootState } from '@state/ducks';
import {
  ProductDetailAction,
  SET_STANDARDS_FEATURES_ICONS,
  SET_STANDARDS_FEATURES_ICONS_LOADING,
} from '@state/ducks/productdetail/productdetail.reducer';
import { useDispatch, useSelector } from 'react-redux';

export interface ProductFeatureIcon {
  iconUrl: string;
  iconText: string;
  iconMarkup?: string;
}

export interface ProductStandardsFeaturesIconsRequest {
  modelAgilityId: number;
  variantAgilityId?: number;
  cultureCode: string;
  published: boolean;
}

export interface ProductStandardsFeaturesIcons {
  standards: ProductFeatureIcon[] | null;
  features: ProductFeatureIcon[] | null;
  care: ProductFeatureIcon[] | null;
  applications: ProductFeatureIcon[] | null;
}

function fetchProductStandardsFeaturesIcons(
  modelAgilityId: number | null,
  variantAgilityId: number | null,
  cultureCode: string | null,
  published: boolean,
): Promise<ProductStandardsFeaturesIcons | null> {
  if (modelAgilityId === null) {
    return Promise.resolve(null);
  }
  if (cultureCode === null || cultureCode === '') {
    return Promise.resolve(null);
  }

  const params: ProductStandardsFeaturesIconsRequest = {
    modelAgilityId,
    variantAgilityId: variantAgilityId ?? undefined,
    cultureCode,
    published,
  };

  return ApiService.request<ProductStandardsFeaturesIcons>({
    method: 'GET',
    baseUrl: UrlResolver.getDomain(),
    controller: 'product-detail',
    slug: 'product-standards-features-icons',
    params,
  });
}

export const useProductStandardsFeaturesIcons = (): boolean => {
  const modelAgilityId = useSelector<RootState, number | null>((state) => state.productDetail.modelAgilityId);
  const variantAgilityId = useSelector<RootState, number | null>(
    (state) => state.productDetail.selectedVariantAgilityId,
  );
  const pageContext = useSelector<RootState, PageContext>((state) => state.pageContext);
  const isLoadingAssets = useSelector<RootState, boolean>((x) => x.productDetail.loadingAssets);
  const dispatch = useDispatch();

  useEffectButNotOnMount(() => {
    dispatch<ProductDetailAction>({ type: SET_STANDARDS_FEATURES_ICONS_LOADING, payload: true });
    fetchProductStandardsFeaturesIcons(
      modelAgilityId,
      variantAgilityId,
      pageContext.documentCulture,
      pageContext.showOnlyPublishedProducts,
    ).then((productIcons) => {
      if (!productIcons) {
        return;
      }

      dispatch<ProductDetailAction>({ type: SET_STANDARDS_FEATURES_ICONS, payload: productIcons });
    });
  }, [variantAgilityId]);

  return isLoadingAssets;
};
